import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowLeft,
  Search,
  ShieldAlert,
  Car,
  FileText,
  Wallet,
  Building2,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import { getClients } from "../api";

import "../styles/unpaidpremium.css";

const UnpaidPremiums = () => {
  const [clients, setClients] =
    useState([]);

  const [searchTerm, setSearchTerm] =
    useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchClients =
      async () => {
        const data =
          await getClients();

        const unpaid =
          data.filter(
            (c) =>
              c.premium_paid?.toLowerCase() ===
              "no"
          );

        setClients(unpaid);
      };

    fetchClients();
  }, []);

  const unpaidPremiumsFilteredClients =
    useMemo(() => {
      return clients
        .filter(
          (c) =>
            c.name
              ?.toLowerCase()
              .includes(
                searchTerm.toLowerCase()
              ) ||
            c.policy_number
              ?.toLowerCase()
              .includes(
                searchTerm.toLowerCase()
              ) ||
            c.vehicle_number
              ?.toLowerCase()
              .includes(
                searchTerm.toLowerCase()
              )
        )

        .sort((a, b) =>
          a.name.localeCompare(
            b.name
          )
        );
    }, [clients, searchTerm]);

  const unpaidPremiumsDisplayedClients =
    searchTerm.trim()
      ? unpaidPremiumsFilteredClients
      : unpaidPremiumsFilteredClients.slice(
          0,
          10
        );

  const unpaidPremiumsFormatDate =
    (dateString) => {
      if (!dateString)
        return "N/A";

      const d = new Date(
        dateString
      );

      if (isNaN(d.getTime()))
        return "N/A";

      return d.toLocaleDateString(
        "en-GB",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    };

  return (
    <div className="unpaid-premiums-modern-page">

      {/* HERO */}

      <div className="unpaid-premiums-modern-hero">

        <div className="unpaid-premiums-modern-left">

          <div className="unpaid-premiums-alert-pill">
            <ShieldAlert size={16} />
            Insurance Alerts
          </div>

          <h1 className="unpaid-premiums-heading">
            Unpaid
            Premiums.
          </h1>

          <p className="unpaid-premiums-description">
            Clients with
            outstanding premium
            balances.
          </p>

          <div className="unpaid-premiums-actions">

            <button
              className="unpaid-premiums-back-button"
              onClick={() =>
                navigate(
                  "/insurance"
                )
              }
            >
              <ArrowLeft size={18} />
              Back to Insurance
            </button>
          </div>
        </div>

        {/* STATS */}

        <div className="unpaid-premiums-stats-grid">

          <div className="unpaid-premiums-stat-card">

            <div className="unpaid-premiums-stat-icon red">
              <ShieldAlert size={22} />
            </div>

            <div>
              <span>
                Unpaid Policies
              </span>

              <h2>
                {
                  clients.length
                }
              </h2>
            </div>
          </div>

          <div className="unpaid-premiums-stat-card">

            <div className="unpaid-premiums-stat-icon teal">
              <Wallet size={22} />
            </div>

            <div>
              <span>
                Displayed
              </span>

              <h2>
                {
                  unpaidPremiumsDisplayedClients.length
                }
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* TOOLBAR */}

      <div className="unpaid-premiums-toolbar">

        <div className="unpaid-premiums-search-box">

          <Search size={18} />

          <input
            type="text"
            placeholder="Search client, vehicle or policy number..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(
                e.target.value
              )
            }
          />
        </div>

        <div className="unpaid-premiums-results">
          {searchTerm.trim()
            ? `${unpaidPremiumsFilteredClients.length} client(s) found`
            : `Showing top 10 clients`}
        </div>
      </div>

      {/* TABLE */}

      <div className="unpaid-premiums-table-wrapper">

        <table className="unpaid-premiums-table">

          <thead>
            <tr>
              <th>
                Client
              </th>

              <th>
                Policy
              </th>

              <th>
                Vehicle
              </th>

              <th>
                Premium
              </th>

              <th>
                Insurer
              </th>

              <th>
                Renewal
              </th>

              <th>
                Status
              </th>
            </tr>
          </thead>

          <tbody>

            {unpaidPremiumsDisplayedClients.length >
            0 ? (
              unpaidPremiumsDisplayedClients.map(
                (
                  c,
                  i
                ) => (
                  <tr key={i}>

                    {/* CLIENT */}

                    <td>

                      <div className="unpaid-premiums-client-cell">

                        <div className="unpaid-premiums-avatar">
                          {c.name
                            ?.charAt(
                              0
                            )
                            ?.toUpperCase()}
                        </div>

                        <div>
                          <h4>
                            {
                              c.name
                            }
                          </h4>

                          <span>
                            {
                              c.policy_type
                            }
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* POLICY */}

                    <td>

                      <div className="unpaid-premiums-info-cell">
                        <FileText size={16} />

                        <span>
                          {
                            c.policy_number
                          }
                        </span>
                      </div>
                    </td>

                    {/* VEHICLE */}

                    <td>

                      <div className="unpaid-premiums-info-cell">
                        <Car size={16} />

                        <span>
                          {
                            c.vehicle_number
                          }
                        </span>
                      </div>
                    </td>

                    {/* PREMIUM */}

                    <td className="unpaid-premiums-amount">
                      $
                      {parseFloat(
                        c.premium ||
                          0
                      ).toLocaleString()}
                    </td>

                    {/* INSURER */}

                    <td>

                      <div className="unpaid-premiums-info-cell">
                        <Building2 size={16} />

                        <span>
                          {
                            c.insurer
                          }
                        </span>
                      </div>
                    </td>

                    {/* RENEWAL */}

                    <td>
                      {unpaidPremiumsFormatDate(
                        c.renewal_date
                      )}
                    </td>

                    {/* STATUS */}

                    <td>
                      <span className="unpaid-premiums-status-pill">
                        Unpaid
                      </span>
                    </td>

                  </tr>
                )
              )
            ) : (
              <tr>

                <td
                  colSpan="7"
                  className="unpaid-premiums-empty-state"
                >

                  <ShieldAlert size={44} />

                  <h3>
                    No Unpaid
                    Policies
                  </h3>

                  <p>
                    No outstanding
                    premium records
                    were found.
                  </p>

                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UnpaidPremiums;